/**
 * Hook for loading and managing a Fluid container connection.
 */
import { getFluidContainerId } from '@datapad/backend-interface';
import { type PresenceManager, createPresenceManager } from '@datapad/data-model';
import type { ITelemetryBaseLogger } from '@fluidframework/azure-client';
import type { ICriticalContainerError, IFluidContainer } from 'fluid-framework';
import React from 'react';
import { containerSchema } from '../Fluid.js';
import { getAzureClient } from '../utilities/index.js';

// TODO: split into separate types for each of the states
/**
 * State returned by {@link useFluidContainer}.
 */
export interface FluidContainerState {
	/**
	 * The loaded Fluid container, or undefined if still loading or errored.
	 */
	readonly container: IFluidContainer | undefined;

	/**
	 * The presence manager, or undefined if the container hasn't loaded yet.
	 */
	readonly presenceManager: PresenceManager | undefined;

	/**
	 * Whether the container is currently loading.
	 */
	readonly isLoading: boolean;

	/**
	 * Error that occurred during loading, if any.
	 */
	readonly error: Error | undefined;
}

/**
 * Hook that loads and manages a Fluid container.
 *
 * @param userName - The signed-in user's name, used for authentication.
 * @returns The container state including loading and error states.
 *
 * @remarks
 * This hook:
 * - Fetches the container ID from the backend
 * - Creates an Azure client and connects to the container
 * - Sets up event listeners for connection state changes
 * - Initializes Fluid Devtools in development mode
 */
export function useFluidContainer(userName: string): FluidContainerState {
	const [container, setContainer] = React.useState<IFluidContainer | undefined>(undefined);
	const [presenceManager, setPresenceManager] = React.useState<PresenceManager | undefined>(
		undefined,
	);
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<Error | undefined>(undefined);

	// Load the container
	React.useEffect(() => {
		let mounted = true;

		async function loadContainer(): Promise<void> {
			try {
				const containerId = await getFluidContainerId();

				if (!containerId) {
					throw new Error('Could not fetch Fluid container ID.');
				}

				// Initialize Devtools logger if in development mode
				let telemetryLogger: ITelemetryBaseLogger | undefined;
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line import/no-extraneous-dependencies -- only used in development mode
					const { createDevtoolsLogger } = await import('@fluidframework/devtools/beta');
					telemetryLogger = createDevtoolsLogger();
				}

				// Get connection to service
				const azureClient = await getAzureClient(userName);

				// Get container
				const { container: loadedContainer } = await azureClient.getContainer(
					containerId,
					containerSchema,
					'2',
				);

				// Initialize Devtools if in development mode
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line import/no-extraneous-dependencies -- only used in development mode
					const { initializeDevtools } = await import('@fluidframework/devtools/beta');
					initializeDevtools({
						initialContainers: [
							{ containerKey: 'Datapad Container', container: loadedContainer },
						],
						logger: telemetryLogger,
					});
				}

				if (mounted) {
					// Initialize presence early so the workspace can exchange state
					// with other clients before React renders content.
					const manager = createPresenceManager(loadedContainer);
					setPresenceManager(manager);
					setContainer(loadedContainer);
					setIsLoading(false);
				}
			} catch (error) {
				if (mounted) {
					setError(error instanceof Error ? error : new Error(String(error)));
					setIsLoading(false);
				}
			}
		}

		loadContainer();

		return () => {
			mounted = false;
		};
	}, [userName]);

	// Set up container event listeners
	React.useEffect(() => {
		if (!container) return;

		function onContainerConnected(): void {
			console.log('Fluid Container connected.');
		}

		function onContainerDisconnected(error?: ICriticalContainerError): void {
			console.log('Fluid Container disconnected.');
			if (error !== undefined) {
				console.error('Fluid Container disconnected due to an error:', error);
			}
		}

		function onContainerDisposed(error?: ICriticalContainerError): void {
			console.error('Fluid Container was disposed.');
			if (error) {
				console.error('Disposal error:', error);
			}
		}

		container.on('connected', onContainerConnected);
		container.on('disconnected', onContainerDisconnected);
		container.on('disposed', onContainerDisposed);

		return () => {
			container.off('connected', onContainerConnected);
			container.off('disconnected', onContainerDisconnected);
			container.off('disposed', onContainerDisposed);
		};
	}, [container]);

	return { container, presenceManager, isLoading, error };
}
